import React, { Fragment, useEffect, useState, useRef, useMemo, useCallback } from "react";
import {
  Container,
  Row,
  Col,
  Card,
  CardBody,
  CardHeader,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Table,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
  Badge,
} from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useDispatch, useSelector } from "react-redux";
import { getAllCustomers, getAllBulkUploadUsers } from "../../../store/slices/customers";
import { getAllPrograms } from "../../../store/slices/programsSlice";
import { useNavigate } from "react-router";
import Papa from "papaparse";
import { saveAs } from "file-saver";
import { toast } from "react-toastify";
import API from "../../../api/baseApi";
import { getAccessToken } from "../../../api/baseApi";
import * as XLSX from "xlsx";
import { CUSTOMER_FIELDS } from "./customerFields";
import ProgramSlugModal from "../../Common/ProgramSlugModal";
import axios from "axios";
import { useDebounce } from "../../../Hooks/useDebounce";

// ─────────────────────────────────────────────────────────────────────────────
// Shared column definitions (same for both tabs)
// ─────────────────────────────────────────────────────────────────────────────
const useCustomerColumns = (onRowClick) =>
  useMemo(
    () => [
      {
        accessorKey: "first_name",
        header: "First Name",
        size: 130,
      },
      {
        accessorKey: "last_name",
        header: "Last Name",
        size: 130,
      },
      {
        accessorKey: "email",
        header: "Email",
        size: 200,
      },
      {
        accessorKey: "phone",
        header: "Mobile No.",
        size: 140,
      },
      {
        accessorKey: "uniqueRegID",
        header: "Unique Reg ID",
        size: 160,
      },
      {
        accessorKey: "address",
        header: "Address",
        size: 160,
      },
      {
        id: "mobile_verify",
        header: "Mobile Verify",
        size: 120,
        enableColumnFilter: false,
        Cell: () => (
          <Badge color="success" pill>
            Verified
          </Badge>
        ),
      },
      {
        id: "email_verify",
        header: "Email Verify",
        size: 110,
        enableColumnFilter: false,
        Cell: () => (
          <Badge color="success" pill>
            Verified
          </Badge>
        ),
      },
      {
        accessorKey: "is_personal_details_fill",
        header: "Details Filled",
        size: 120,
        enableColumnFilter: false,
        Cell: ({ cell }) =>
          cell.getValue() ? (
            <Badge color="success" pill>Yes</Badge>
          ) : (
            <Badge color="warning" pill>No</Badge>
          ),
      },
      {
        accessorKey: "isActive",
        header: "Account Active",
        size: 120,
        enableColumnFilter: false,
        Cell: ({ cell }) =>
          cell.getValue() ? (
            <Badge color="success" pill>Active</Badge>
          ) : (
            <Badge color="secondary" pill>Inactive</Badge>
          ),
      },
      {
        accessorKey: "activation_mail_sent",
        header: "Activation Mail",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) =>
          cell.getValue() ? (
            <Badge color="info" pill>Sent</Badge>
          ) : (
            <Badge color="warning" pill>Not Sent</Badge>
          ),
      },
      {
        id: "program_name",
        header: "Enrolled Program",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const subs = row.original.CustomerSubscription || [];
          return subs[0]?.Programs?.name || "—";
        },
      },
      {
        id: "expiration_date",
        header: "Expiry Date",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ row }) => {
          const services = row.original.CustomerServices || [];
          const dateStr = services[0]?.expiration_date;
          return dateStr ? new Date(dateStr).toLocaleDateString() : "—";
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

// ─────────────────────────────────────────────────────────────────────────────
// Generic customer data table (used by both tabs)
// ─────────────────────────────────────────────────────────────────────────────
const CustomerDataTable = ({ sourceFilter }) => {
  const dispatch = useDispatch();
  const history = useNavigate();
  const client = useSelector((state) => state.clients.selectedClient.value);

  const [data, setData] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const [globalSearch, setGlobalSearch] = useState("");
  const debouncedSearch = useDebounce(globalSearch, 300);
  const columnFilterSearch = useDebounce(columnFilters, 300);
  const [isLoading, setIsLoading] = useState(false);

  const fetchData = useCallback(async () => {
    const sortField = sorting.length > 0 ? sorting[0].id : undefined;
    const sortOrder =
      sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : undefined;

    setIsLoading(true);
    try {
      const res = await dispatch(
        getAllCustomers({
          pagination: true,
          limit: pageSize,
          offset: page * pageSize,
          sortBy: sortField,
          sortOrder,
          source_filter: sourceFilter,   // 'direct' | 'bulk'
          search: debouncedSearch || undefined,
          filters: columnFilterSearch,
        }),
      );
      if (res?.payload) {
        const p = res.payload;
        setData(p.customers || p.result || p.data || []);
        setTotalCount(
          p.count !== undefined ? p.count : p.total !== undefined ? p.total : 0,
        );
      }
    } finally {
      setIsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, client, page, pageSize, sorting, debouncedSearch, columnFilterSearch, sourceFilter]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Reset to page 0 on filter/search change
  useEffect(() => { setPage(0); }, [debouncedSearch, columnFilterSearch]);

  const handleRowClick = (row) => {
    history(`/app/contact-app/contacts`);
  };

  const columns = useCustomerColumns(handleRowClick);

  const table = useMaterialReactTable({
    columns,
    data,
    initialState: { density: "compact" },
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: totalCount || 0,
    onPaginationChange: (updater) => {
      const newState =
        typeof updater === "function"
          ? updater({ pageIndex: page, pageSize })
          : updater;
      setPage(newState.pageIndex);
      setPageSize(newState.pageSize);
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalSearch,
    enableGlobalFilter: true,
    state: {
      pagination: { pageIndex: page, pageSize },
      sorting,
      columnFilters,
      globalFilter: globalSearch,
      isLoading,
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleRowClick(row),
      sx: { cursor: "pointer" },
    }),
  });

  return <MaterialReactTable table={table} />;
};

// ─────────────────────────────────────────────────────────────────────────────
// PaginatedProgramTable (unchanged helper)
// ─────────────────────────────────────────────────────────────────────────────
const PaginatedProgramTable = ({ programList }) => {
  const columns = React.useMemo(
    () => [
      { accessorKey: "name", header: "Program Name" },
      { accessorKey: "slug", header: "Slug Name" },
    ],
    [],
  );
  const table = useMaterialReactTable({
    columns,
    data: programList,
    initialState: { density: "compact", pagination: { pageIndex: 0, pageSize: 10 } },
    enableSorting: false,
    enableColumnFilters: false,
    enableGlobalFilter: false,
    enableRowActions: false,
    enableTopToolbar: false,
    enableBottomToolbar: true,
    muiTableContainerProps: { sx: { minHeight: "340px" } },
  });
  return <MaterialReactTable table={table} />;
};

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
const CustomersTable = () => {
  const dispatch = useDispatch();
  const inputRef = useRef(null);
  const [csv, setCsv] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isTemplateDownloadLoading, setIsTemplateDownloadLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("1");

  // Mapping modal
  const [showMappingModal, setShowMappingModal] = useState(false);
  const [uploadedHeaders, setUploadedHeaders] = useState([]);
  const [fieldMapping, setFieldMapping] = useState({});
  const [mappingErrors, setMappingErrors] = useState({});

  // Slug modal
  const [showSlugModal, setShowSlugModal] = useState(false);
  const [programList, setProgramList] = useState([]);
  // Generic "Programs & Slugs" modal (with copy) shown next to Get Template
  const [programSlugOpen, setProgramSlugOpen] = useState(false);

  const client = useSelector((state) => state.clients.selectedClient.value);
  const tenantClientData = useSelector((state) => state.clients);

  const UploadAllowTenant = ["hc", "ebix", "vahan", "cyberior"];
  const SlugNameAllowTenant = ["vahan", "hc"];

  // ── File parsing ────────────────────────────────────────────────────────
  const parseExcelFile = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: "array" });
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
          if (jsonData?.length === 0) { reject(new Error("Excel file is empty")); return; }
          const headers = jsonData[0].map((h) => String(h || "").trim()).filter((h) => h);
          const rows = jsonData
            .slice(1)
            .map((row) => {
              const obj = {};
              headers.forEach((header, index) => { obj[header] = row[index] !== undefined ? String(row[index] || "").trim() : ""; });
              return obj;
            })
            .filter((row) => Object.values(row).some((v) => v));
          resolve({ headers, data: rows });
        } catch (err) { reject(err); }
      };
      reader.onerror = reject;
      reader.readAsArrayBuffer(file);
    });

  const parseCSVFile = (file) =>
    new Promise((resolve, reject) => {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        transform: (value) => (typeof value === "string" ? value.trim() : value),
        complete: (results) => {
          if (results?.errors?.length > 0 && results?.data?.length === 0) { reject(new Error("Error parsing CSV file")); return; }
          const headers = Object.keys(results.data[0] || {});
          resolve({ headers, data: results.data });
        },
        error: reject,
      });
    });

  const handleFileChange = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size should be less than 5MB");
      if (inputRef.current) inputRef.current.value = "";
      setCsv(null);
      return;
    }
    const isCSV = file.type === "text/csv" || file.name.endsWith(".csv");
    const isExcel =
      file.type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
      file.type === "application/vnd.ms-excel" ||
      file.name.endsWith(".xlsx") ||
      file.name.endsWith(".xls");
    if (!isCSV && !isExcel) {
      toast.error("Please upload a valid CSV or Excel file (.csv, .xlsx, .xls)");
      if (inputRef.current) inputRef.current.value = "";
      setCsv(null);
      return;
    }
    try {
      setCsv(file);
      setError(null);
      const result = isCSV ? await parseCSVFile(file) : await parseExcelFile(file);
      if (result?.data?.length > 1001) {
        toast.error("Please upload a file with less than 1000 records.");
        if (inputRef.current) inputRef.current.value = "";
        setCsv(null);
        return;
      }
      setUploadedHeaders(result.headers);
      const initialMapping = {};
      CUSTOMER_FIELDS.forEach((field) => {
        initialMapping[field.key] = result?.headers?.includes(field.key) ? field.key : "";
      });
      setFieldMapping(initialMapping);
      setMappingErrors({});
      setShowMappingModal(true);
    } catch (err) {
      toast.error(`Error parsing file: ${err.message}`);
      if (inputRef.current) inputRef.current.value = "";
      setCsv(null);
    }
  };

  // ── Field mapping helpers ──────────────────────────────────────────────
  const getSortedCustomerFields = () => {
    const required = CUSTOMER_FIELDS.filter((f) => f.required);
    const optional = CUSTOMER_FIELDS.filter((f) => !f.required);
    return [...required, ...optional];
  };

  const getAvailableUploadedHeaders = (currentField) => {
    const currentMapped = fieldMapping[currentField] || "";
    const allMapped = Object.values(fieldMapping).filter(Boolean);
    return uploadedHeaders.filter(
      (h) => h === currentMapped || !allMapped.includes(h),
    );
  };

  const handleMappingChange = (dbField, uploadedHeader) => {
    setFieldMapping((prev) => ({ ...prev, [dbField]: uploadedHeader }));
    if (mappingErrors[dbField]) {
      setMappingErrors((prev) => { const e = { ...prev }; delete e[dbField]; return e; });
    }
  };

  const validateMapping = () => {
    const errors = {};
    CUSTOMER_FIELDS.filter((f) => f.required).forEach((field) => {
      if (!fieldMapping[field.key]) errors[field.key] = `${field.label} is required but not mapped`;
    });
    setMappingErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ── Upload ─────────────────────────────────────────────────────────────
  const handleFinalUpload = async () => {
    if (!validateMapping()) { toast.error("Please map all required fields before uploading."); return; }
    if (!csv) { toast.error("No file selected."); return; }
    setIsLoading(true);
    try {
      const formData = new FormData();
      formData.append("files", csv);
      formData.append("mappingJson", JSON.stringify(fieldMapping));
      const response = await axios.post(
        `${process.env.REACT_APP_PL_API}/customers/bulkupload_user-new`,
        formData,
        {
          headers: {
            "x-tenant-id": client,
            "x-access-token": getAccessToken(client),
          },
          maxBodyLength: Infinity,
          maxContentLength: Infinity,
          responseType: "blob",
        },
      );
      saveAs(response.data, "upload_result.csv");
      setCsv(null);
      setShowMappingModal(false);
      setFieldMapping({});
      setUploadedHeaders([]);
      if (inputRef.current) inputRef.current.value = "";
      toast.success("File uploaded and result downloaded successfully");
    } catch (err) {
      toast.error(`Error uploading file: ${err.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseMappingModal = () => {
    setShowMappingModal(false);
    setCsv(null);
    setFieldMapping({});
    setUploadedHeaders([]);
    setMappingErrors({});
    if (inputRef.current) inputRef.current.value = "";
  };

  const handleDownloadTemplate = () => {
    setIsTemplateDownloadLoading(true);
    try {
      const csvContent = CUSTOMER_FIELDS.map((el) => el.key).join(",");
      saveAs(new Blob([csvContent], { type: "text/csv;charset=utf-8" }), "Get-Template.csv");
    } finally {
      setIsTemplateDownloadLoading(false);
    }
  };

  const showUpload = UploadAllowTenant.includes(tenantClientData?.selectedClient?.value);

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Customers" parent="Customers" parentPath="customers" title="Customers List" />

      <Container fluid={true}>
        {/* ── Upload card ── */}
        {showUpload && (
          <Row>
            <Col sm="12">
              <Card>
                <CardHeader className="pb-2">
                  <h6 className="mb-0">Bulk Upload CSV</h6>
                </CardHeader>
                <CardBody>
                  <FormGroup>
                    <Label for="csvFile">Upload CSV / Excel</Label>
                    <Input
                      type="file"
                      id="csvFile"
                      accept=".csv,.xlsx,.xls"
                      onChange={handleFileChange}
                      innerRef={inputRef}
                    />
                    <small className="form-text text-muted">
                      Supported: CSV, XLSX, XLS · Max 5MB · Max 1000 records. Each row's Program
                      Slug determines which Client the customer belongs to.
                    </small>
                  </FormGroup>
                  {error && <Alert color="danger">{error}</Alert>}
                  {success && <Alert color="success">{success}</Alert>}
                  <div className="d-flex gap-2 flex-wrap">
                    <Button color="info" onClick={handleDownloadTemplate} size="sm">
                      {isTemplateDownloadLoading ? "Downloading…" : "Get Template"}
                    </Button>
                    <Button
                      color="outline-secondary"
                      size="sm"
                      onClick={() => setProgramSlugOpen(true)}
                      title="View program slug names"
                    >
                      📋 Programs &amp; Slugs
                    </Button>
                    <ProgramSlugModal
                      isOpen={programSlugOpen}
                      toggle={() => setProgramSlugOpen(false)}
                    />
                    {SlugNameAllowTenant.includes(client) && (
                      <Button
                        color="primary"
                        size="sm"
                        onClick={async () => {
                          try {
                            const res = await dispatch(getAllPrograms({ getBySlugName: true, tenantId: client, accessToken: getAccessToken(client) }));
                            const data = res?.payload || [];
                            const list = Array.isArray(data)
                              ? data.map((p) => ({ name: p.name || "", slug: p.slug_name || p.slug || "" })).filter((p) => p.name && p.slug)
                              : [];
                            setProgramList(list);
                            setShowSlugModal(true);
                          } catch {
                            setProgramList([]);
                            setShowSlugModal(true);
                          }
                        }}
                      >
                        View Program Slug Names
                      </Button>
                    )}
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        )}

        {/* ── Tabs ── */}
        <Row>
          <Col sm="12">
            <Card>
              <Nav tabs className="border-bottom-0 ps-3 pt-3">
                <NavItem>
                  <NavLink
                    className={activeTab === "2" ? "active fw-semibold" : ""}
                    onClick={() => setActiveTab("2")}
                    style={{
                      cursor: "pointer",
                      border: "1px solid #dee2e6",
                      borderBottom: activeTab === "2" ? "1px solid #fff" : "1px solid #dee2e6",
                      backgroundColor: activeTab === "2" ? "#fff" : "#f8f9fa",
                    }}
                  >
                    👤 B2C
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink
                    className={activeTab === "1" ? "active fw-semibold" : ""}
                    onClick={() => setActiveTab("1")}
                    style={{
                      cursor: "pointer",
                      border: "1px solid #dee2e6",
                      borderBottom: activeTab === "1" ? "1px solid #fff" : "1px solid #dee2e6",
                      marginLeft: 4,
                      backgroundColor: activeTab === "1" ? "#fff" : "#f8f9fa",
                    }}
                  >
                    📤 B2B
                  </NavLink>
                </NavItem>
              </Nav>
              <TabContent activeTab={activeTab}>
                <TabPane tabId="1">
                  {/* B2B = customers with createdVia = 'sFTP' */}
                  {activeTab === "1" && <CustomerDataTable sourceFilter="bulk" />}
                </TabPane>
                <TabPane tabId="2">
                  {/* B2C = customers with createdVia = 'manual' or null */}
                  {activeTab === "2" && <CustomerDataTable sourceFilter="direct" />}
                </TabPane>
              </TabContent>
            </Card>
          </Col>
        </Row>
      </Container>

      {/* ── Slug Modal ── */}
      <Modal isOpen={showSlugModal} toggle={() => setShowSlugModal(false)} size="xl">
        <ModalHeader toggle={() => setShowSlugModal(false)}>Program Name &amp; Slug Names</ModalHeader>
        <ModalBody style={{ minHeight: "460px" }}>
          <PaginatedProgramTable programList={programList || []} />
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setShowSlugModal(false)}>Close</Button>
        </ModalFooter>
      </Modal>

      {/* ── Field Mapping Modal ── */}
      <Modal isOpen={showMappingModal} toggle={handleCloseMappingModal} size="xl">
        <ModalHeader toggle={handleCloseMappingModal}>Map Fields to Database</ModalHeader>
        <ModalBody>
          <Alert color="info" className="bg-primary bg-opacity-25">
            Select which column from your uploaded file maps to each database field.
            Fields marked <span className="text-danger">*</span> are required.
          </Alert>
          <div style={{ maxHeight: "60vh", overflowY: "auto" }}>
            <Table responsive striped>
              <thead>
                <tr>
                  <th>Database Field</th>
                  <th>Select Uploaded Field</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {getSortedCustomerFields().map((field) => {
                  const mappedHeader = fieldMapping[field.key] || "";
                  const isRequired = field.required || false;
                  const isMapped = !!mappedHeader;
                  const hasError = mappingErrors[field.key];
                  return (
                    <tr key={field.key} style={isRequired ? { backgroundColor: "#fff5f5" } : {}}>
                      <td>
                        <strong>
                          {field.label}
                          {isRequired && <span className="text-danger ms-1">*</span>}
                        </strong>
                        <br />
                        <small className="text-muted">({field.key})</small>
                      </td>
                      <td>
                        <Input
                          type="select"
                          value={mappedHeader}
                          onChange={(e) => handleMappingChange(field.key, e.target.value)}
                          className={hasError || (isRequired && !isMapped) ? "border-danger" : ""}
                        >
                          <option value="">-- Select Uploaded Field --</option>
                          {getAvailableUploadedHeaders(field.key).map((h) => (
                            <option key={h} value={h}>{h}</option>
                          ))}
                        </Input>
                        {hasError && <small className="text-danger d-block mt-1">{mappingErrors[field.key]}</small>}
                      </td>
                      <td>
                        {isRequired ? (
                          <Badge color={isMapped ? "success" : "danger"} pill>
                            {isMapped ? "Mapped" : "Required"}
                          </Badge>
                        ) : (
                          <Badge color={isMapped ? "info" : "secondary"} pill>
                            {isMapped ? "Mapped" : "Optional"}
                          </Badge>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
          {Object.keys(mappingErrors).length > 0 && (
            <Alert color="danger">
              <strong>Mapping Errors:</strong>
              <ul className="mb-0">
                {Object.values(mappingErrors).map((e, i) => <li key={i}>{e}</li>)}
              </ul>
            </Alert>
          )}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" disabled={isLoading} onClick={handleCloseMappingModal}>Cancel</Button>
          <Button color="primary" disabled={isLoading} onClick={handleFinalUpload}>
            {isLoading ? "Uploading…" : "Upload with Mapping"}
          </Button>
        </ModalFooter>
      </Modal>
    </Fragment>
  );
};

export default CustomersTable;
